/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import { isString } from 'lodash';
import {
  forwardDeskingDealIdChangedToCC,
  updateDeskingTabChangedToCC,
} from 'util/commandCenter';
import { safeParseStringEventObject, sendData } from 'util/amplitude';
import {
  amplitudeEventsDeskingCTR,
  amplitudeEventsVehicleselection,
  VehicleSelectedAction,
} from 'util/amplitudeEvents';
import { useCommandCenterDeskingRedirect } from 'hooks/useCommandCenterDeskingRedirect';
import CarPencilEditor from './CarPencilEditor';
import dealsActions from '../../redux/deals/actions';
import pencilsActions from '../../redux/pencils/actions';
import globalActions from '../../redux/global/actions';
import * as deskingActions from '../../redux/desking/actions';
import penActions from '../../redux/pen/actions';
import useFetchDealOwner from '../../components/PencilEditor/useFetchDealOwner';
import NewDeal from './NewDeal';

function PencilEditor(props) {
  const {
    actions,
    selectedCars,
    match,
    pencils,
    pencil,
    deal,
    dealer,
    location,
    isStandaloneNotesViewer,
    user,
  } = props;
  const { dealId } = match.params;
  const [currentSelectedCar, setCar] = useState(null);

  const car = useMemo(() => {
    if (!currentSelectedCar) {
      return selectedCars?.[0];
    }
    // because we will be updating the mileage of the car, we want to use the selectedCars from the deal instead of the initial selectedCar that was set
    if (currentSelectedCar && selectedCars?.length) {
      const matchingSelectedCar = selectedCars.find(
        c => c.id === currentSelectedCar.id,
      );

      // if the currentlySelectedCar is not in the selectedCars list, it's been removed, so just default back to first
      // car in the list
      if (!matchingSelectedCar) {
        setCar(selectedCars[0]);
      } else {
        return matchingSelectedCar;
      }
    }
    return currentSelectedCar;
  }, [currentSelectedCar, selectedCars]);

  const { dealOwner } = useFetchDealOwner({ car, dealer });

  useCommandCenterDeskingRedirect({
    user,
    redirectUrl: `${process.env.REACT_APP_COMMAND_CENTER_URL}/deals/desking/${dealId}`,
  });

  useEffect(() => {
    const searchParams = isString(location?.search)
      ? Object.fromEntries(new URLSearchParams(location.search))
      : {};

    sendData(
      amplitudeEventsDeskingCTR.DESKING_DEAL_PAGE_VIEW,
      safeParseStringEventObject({
        dealId,
        deskingTab: match.params.currentTab,
        ...searchParams,
      }),
    );
  }, []);

  useEffect(() => {
    actions.initPenConfigRequest({ dealer: dealOwner ?? dealer });
  }, [actions, dealOwner, dealer]);

  useEffect(() => {
    if (!dealer && dealId) {
      actions.loadDealData({ dealId });
    }
  }, [dealer, dealId]);

  useEffect(() => {
    // Load deal if id is provided
    if (dealId) {
      forwardDeskingDealIdChangedToCC(dealId);

      actions.dealRequest({ dealId });
    }
  }, [dealId, actions]);

  useEffect(() => {
    if (!dealId) {
      // Avoid calling update on new pencil page
      return;
    }
    updateDeskingTabChangedToCC({
      dealId,
      tab: match.params.currentTab,
    });
  }, [dealId, match.path]);

  const selectCar = useCallback(
    selectedCar => {
      // Reset data
      setCar(selectedCar);
      actions.setPencil(null);
      actions.setDesking(null);
      actions.paymentsReset();
      actions.pencilListReset();
    },
    [actions],
  );

  const onRemoveCar = useCallback(() => {
    selectCar(selectedCars[0]);
  }, [selectedCars, selectCar]);

  // If no deal id was provided - allow to create a new deal
  if (!dealId) {
    return <NewDeal />;
  }

  if (!dealer) {
    return null;
  }

  // allow user to desk deal in CarPencilEditor even if there is no vehicle on the deal (so selectedCars will never have values)
  if (deal && !deal.car_id) {
    return (
      <CarPencilEditor
        isStandaloneNotesViewer={isStandaloneNotesViewer}
        onCarSelect={selectedCar => {
          selectCar(selectedCar);
          sendData(
            amplitudeEventsVehicleselection[
              VehicleSelectedAction.ADDOTHER_FINISH
            ],
            {
              carMake: selectedCar.make,
              carModel: selectedCar.model,
              carYear: selectedCar.year,
            },
          );
          sendData(
            amplitudeEventsVehicleselection[VehicleSelectedAction.FINISH],
            {
              carMake: selectedCar.make,
              carModel: selectedCar.model,
              carYear: selectedCar.year,
            },
          );
        }}
        onRemoveCar={onRemoveCar}
      />
    );
  }

  return selectedCars.map(c => {
    if (car && car.id === c.id) {
      // Extract pencils for the current car
      const pencilsForCar = pencils
        ? pencils.filter(p => p.carId === car.id)
        : null;

      return (
        <CarPencilEditor
          isStandaloneNotesViewer={isStandaloneNotesViewer}
          key={c.id}
          car={c}
          pencil={pencil}
          onRemoveCar={onRemoveCar}
          pencils={pencilsForCar}
          onCarSelect={selectedCar => {
            selectCar(selectedCar);
            sendData(
              amplitudeEventsVehicleselection[
                VehicleSelectedAction.ADDOTHER_FINISH
              ],
              {
                carMake: selectedCar.make,
                carModel: selectedCar.model,
                carYear: selectedCar.year,
              },
            );
            sendData(
              amplitudeEventsVehicleselection[VehicleSelectedAction.FINISH],
              {
                carMake: selectedCar.make,
                carModel: selectedCar.model,
                carYear: selectedCar.year,
              },
            );
          }}
        />
      );
    }
    return null;
  });
}

const mapStateToProps = ({ deals, pencils, user }) => ({
  pencils: pencils.data,
  selectedCars: deals.selectedCars,
  car: deals.car,
  pencil: deals.pencil,
  user: user.currentUser,
  deal: deals.deal,
  dealer: deals.dealer,
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      ...dealsActions,
      ...pencilsActions,
      ...deskingActions,
      ...penActions,
      ...globalActions,
    },
    dispatch,
  ),
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(PencilEditor),
);
